import React from 'react';

/**
 * PageHeader — shared across all app pages (History, Cases, Reports, Admin, Demo, Settings).
 *
 * Upgraded to the liquid-glass material system (Phase 1 redesign):
 * - translucent glass surface with inner top sheen
 * - luminous icon badge on the left
 * - large, high-contrast Outfit-font title (text-2xl → text-3xl responsive)
 * - subtitle reads cleanly without competing
 * - optional actions slot (right)
 *
 * Preserves existing API exactly: { icon, iconBg, title, subtitle, actions, className }.
 * The `iconBg` prop is accepted for backward compat but no longer used — badge styling
 * is now standardised via `.liquid-header` tokens. Removing it is a future cleanup.
 */
export function PageHeader({ icon, iconBg, title, subtitle, actions, className = '' }) {
  // eslint-disable-next-line no-unused-vars
  const _iconBg = iconBg; // intentionally unused — kept for API parity
  return (
    <header className={`liquid-header mb-6 ${className}`} data-testid="page-header">
      <div className="relative flex items-start gap-4 sm:gap-5">
        {icon && (
          <div
            className="shrink-0 mt-1 w-11 h-11 rounded-2xl flex items-center justify-center text-[var(--ink-accent)] border border-[var(--glass-stroke-strong)] bg-[linear-gradient(135deg,rgba(255,255,255,0.12),rgba(255,255,255,0.02))] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_8px_18px_hsl(var(--accent-cyan)/0.18)]"
            aria-hidden
            data-testid="page-header-icon"
          >
            {React.isValidElement(icon)
              ? React.cloneElement(icon, { className: 'w-5 h-5' })
              : icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="liquid-header__title" data-testid="page-title">{title}</h1>
          {subtitle && (
            <p className="liquid-header__subtitle" data-testid="page-subtitle">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="shrink-0 flex items-center gap-2" data-testid="page-header-actions">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}

export default PageHeader;
